<template>
  <!-- Loading overlay: semi-transparent so admin content stays visible; centered logo + spinner -->
  <div
    v-if="appLoading"
    class="fixed inset-0 z-[100] flex items-center justify-center font-display bg-background-light/85 dark:bg-background-dark/85 backdrop-blur-sm"
    aria-live="polite"
    aria-busy="true"
  >
    <div class="text-center">
      <img
        src="/logos/QRestoo-app-logo--rounded.png"
        alt=""
        class="w-20 h-20 md:w-24 md:h-24 mx-auto rounded-xl object-contain mb-4"
        width="96"
        height="96"
      />
      <div class="w-12 h-12 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-3">
        <span class="material-icons text-2xl text-primary animate-spin" aria-hidden="true">sync</span>
      </div>
      <p class="text-charcoal/60 dark:text-white/60 text-sm">Loading…</p>
    </div>
  </div>
  <div class="min-h-screen">
    <ToastContainer />
    <router-view />
    <PaidFeatureModal />
    <SitePreferencesBar />
    <SitePreferencesModal />
  </div>
</template>

<script setup>
import { computed, onMounted, watch } from 'vue'
import { useAppStore } from '@/stores/app'
import { useCookieConsentStore } from '@/stores/sitePrefs'
import { useRoute, useRouter } from 'vue-router'
import { syncGa4WithConsent } from '@/utils/gtagBootstrap'
import ToastContainer from '@/components/ui/ToastContainer.vue'
import PaidFeatureModal from '@/components/ui/PaidFeatureModal.vue'
import SitePreferencesBar from '@/components/legal/SitePreferencesBar.vue'
import SitePreferencesModal from '@/components/legal/SitePreferencesModal.vue'

const appStore = useAppStore()
const cookieConsentStore = useCookieConsentStore()
const route = useRoute()
const router = useRouter()

const isProtectedRoute = computed(() => route.matched.some((r) => r.meta.requiresAuth || r.meta.requiresVerified || r.meta.requiresSuperadmin))
const appLoading = computed(() => isProtectedRoute.value && appStore.isAuthBootstrapping && !appStore.isAuthBootstrapped)

onMounted(async () => {
  // Ensure we attempt refresh-on-boot even on public routes.
  // Protected routes will still block via router guards; guest routes can render immediately.
  appStore.bootstrapAuth().catch(() => {})
})

watch(
  () => cookieConsentStore.allowsAnalytics,
  (allowed) => {
    syncGa4WithConsent(!!allowed).catch((e) => {
      if (import.meta.env.DEV) console.warn('[ga4] syncGa4WithConsent failed', e)
    })
  },
  { immediate: true }
)

// If we soft-loaded a guest/public page but bootstrap later restores auth,
// redirect away from guest routes without requiring a full reload.
watch(
  () => [appStore.isAuthBootstrapped, appStore.user?.uuid, route.fullPath],
  () => {
    if (!appStore.isAuthBootstrapped) return
    if (!route.matched.some((r) => r.meta.guest)) return
    if (route.name === 'PublicRestaurant') return
    if (!appStore.user) return
    const isVerified = appStore.user?.isEmailVerified ?? false
    if (isVerified) {
      router.replace({ name: 'App' })
      return
    }
    const email = appStore.user.email
    router.replace(email ? { name: 'VerifyEmail', query: { email } } : { name: 'VerifyEmail' })
  }
)
</script>
